import { VideoData } from '@lumen5/luminary';

import { intToHex } from 'js/src/libs/color-utils';

/**
 * Resolves theme reference strings (e.g. `themeRef_colors_N`) in an ECharts config tree into
 * real hex colors using the current video template's color palette.
 *
 * Used by thumbnails / previews which render through vanilla ECharts and don't go through the
 * luminary ChartRenderer. On the canvas, the chart asset uses the same theme reference strings
 * and luminary resolves them via its template-color pipeline (same "color shuffle" mechanism
 * other assets use).
 */

const NEAR_WHITE_CHANNEL_THRESHOLD = 240;

/**
 * True if a color's R, G, B channels are all >= 240. Used to filter out whites/near-whites
 * from the preview palette — the preview background is white, so we'd render invisible bars.
 */
export function isNearWhiteColor(color: number): boolean {
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    return (
        r >= NEAR_WHITE_CHANNEL_THRESHOLD &&
        g >= NEAR_WHITE_CHANNEL_THRESHOLD &&
        b >= NEAR_WHITE_CHANNEL_THRESHOLD
    );
}

function resolveTemplateColor(value: string, templateColors: number[]): string {
    if (!VideoData.isTemplateReference(value)) return value;
    const idx = VideoData.getTemplateIndex(value);
    if (!Number.isFinite(idx) || templateColors.length === 0) return value;
    const wrapped = ((idx % templateColors.length) + templateColors.length) % templateColors.length;
    return intToHex(templateColors[wrapped]);
}

/**
 * Resolve a single string value — returns the hex color if it's a template color reference,
 * otherwise returns the value unchanged.
 */
export function resolveThemeRefString(value: string, templateColors: number[]): string {
    return resolveTemplateColor(value, templateColors);
}

/**
 * Walks the config tree and returns a new tree with all template color references resolved
 * against the current template colors. Functions (formatters, animationDelay callbacks) are
 * preserved by reference.
 */
export function resolveThemeRefs<T>(node: T, templateColors: number[]): T {
    if (typeof node === 'string') {
        return resolveTemplateColor(node, templateColors) as unknown as T;
    }
    if (typeof node === 'function' || node === null || node === undefined) {
        return node;
    }
    if (Array.isArray(node)) {
        return node.map((item) => resolveThemeRefs(item, templateColors)) as unknown as T;
    }
    if (typeof node === 'object') {
        const result: Record<string, unknown> = {};
        for (const key of Object.keys(node as Record<string, unknown>)) {
            result[key] = resolveThemeRefs((node as Record<string, unknown>)[key], templateColors);
        }
        return result as T;
    }
    return node;
}
